import { diffDays, formatTokens, isSameDay, parseDate, startOf } from 'helpers/date_utils';

import type { Activity } from './types';

export interface TimelineGroup {
  label: string;
  activities: Activity[];
}

// Activity dates are calendar dates ("2026-09-27"), compared against the viewer's local today
export function timelineGroupLabel(date: string, now: Date = new Date()): string {
  const day = parseDate(date);
  if (!day) return 'Undated';

  const today = startOf(now, 'day');

  if (day > today) return 'Upcoming';
  if (day.getTime() === today.getTime()) return 'Today';
  // A rolling week, so Monday mornings don't push yesterday under "Earlier this month"
  if (diffDays(today, day) < 7) return 'This week';
  if (day >= startOf(today, 'month')) return 'Earlier this month';
  if (day >= startOf(today, 'year')) return 'Earlier this year';

  return String(day.getFullYear());
}

// Expects activities already in timeline order (newest first), so each label forms one run
export function groupByPeriod(activities: Activity[], now: Date = new Date()): TimelineGroup[] {
  return activities.reduce<TimelineGroup[]>((groups, activity) => {
    const label = timelineGroupLabel(activity.date, now);
    const current = groups[groups.length - 1];

    if (current?.label === label) {
      current.activities.push(activity);
    } else {
      groups.push({ label, activities: [activity] });
    }

    return groups;
  }, []);
}

// An activity only carries a calendar date, so the clock time is known only when it was logged on
// the day it happened (createdAt falls on that date)
function loggedAt(activity: Activity): Date | null {
  const day = parseDate(activity.date);
  const created = parseDate(activity.createdAt);

  return day && created && isSameDay(day, created) ? created : null;
}

// "5m ago" / "7h ago" for today, "3d ago" within the week, then "Sep 3" or "Sep 3, 2025"
export function timelineTimeLabel(activity: Activity, now: Date = new Date()): string {
  const day = parseDate(activity.date);
  if (!day) return '';

  const time = loggedAt(activity);
  if (time && isSameDay(time, now)) {
    const minutes = Math.floor((now.getTime() - time.getTime()) / 60000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;

    return `${Math.floor(minutes / 60)}h ago`;
  }

  const days = diffDays(now, day);
  if (days === 0) return 'Today';
  if (days > 0 && days < 7) return `${days}d ago`;

  return formatTokens(day, day.getFullYear() === now.getFullYear() ? 'MMM D' : 'MMM D, YYYY');
}

// Full date for the time label's tooltip, e.g. "Sun, Sep 27, 2026, 1:08 PM"
export function timelineTimeTitle(activity: Activity): string {
  const time = loggedAt(activity);
  if (time) return formatTokens(time, 'ddd, MMM D, YYYY, h:mm A');

  const day = parseDate(activity.date);
  return day ? formatTokens(day, 'ddd, MMM D, YYYY') : '';
}
